import {useCallback, useEffect, useMemo, useState} from 'react';

import {
    getCheckedIds,
    getExportOptions,
    getPluginsAndApps,
} from 'src/components/features-section';
import {licenseFeatureModuleToTreeStructure} from 'src/modules/create-account/steps/license-create/utils';
import {getDefaultLicenseFeatures} from 'src/openapi/admin/apis/GetDefaultLicenseFeaturesApi';
import {getLicenseAvailableFeatures} from 'src/openapi/admin/apis/GetLicenseAvailableFeaturesApi';
import {saveLicenseFeatures} from 'src/openapi/admin/apis/SaveLicenseAvailableFeaturesApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {TreeStructure} from '@pb/kit-component-tree-v2';
import type {LicenseType} from 'src/openapi/admin/models/LicenseType';

type Props = {
    licenseId?: number,
    accountId: number,
    licenseType?: LicenseType,
    disabled?: boolean,
};

export const usePlatformFeatures = ({licenseId, accountId, licenseType, disabled}: Props) => {
    const apiDispatch = useAppDispatch();

    const [loading, setLoading] = useState(true);
    const [pluginsAppsTree, setPluginsAppsTree] = useState<TreeStructure | null>(null);
    const [exportOptionsTree, setExportOptionsTree] = useState<TreeStructure | null>(null);
    const [defaultPlatformFeatures, setDefaultPlatformFeatures] = useState<Array<string> | null>(null);
    const [initialPlatformFeatures, setInitialPlatformFeatures] = useState<Array<string> | null>(null);
    const [platformFeatures, setPlatformFeatures] = useState<Array<string>>([]);

    useEffect(() => {
        let loadedDefaultPlatformFeatures: Array<string> = [];

        const loadPlatformFeatures = async (licenseTypeCode: LicenseType) => {
            setLoading(true);
            const defaultPlatformFeaturesResponse = await apiDispatch(getDefaultLicenseFeatures({
                licenseTypeCode,
                accountId,
            }));
            if (defaultPlatformFeaturesResponse.payload.success) {
                const pluginsAndApps = getPluginsAndApps(defaultPlatformFeaturesResponse.payload.data);
                setPluginsAppsTree(licenseFeatureModuleToTreeStructure(pluginsAndApps, disabled));

                const exportOptions = getExportOptions(defaultPlatformFeaturesResponse.payload.data);
                setExportOptionsTree(licenseFeatureModuleToTreeStructure(exportOptions, disabled));

                loadedDefaultPlatformFeatures = getCheckedIds(defaultPlatformFeaturesResponse.payload.data);
                setDefaultPlatformFeatures(loadedDefaultPlatformFeatures);
            }

            if (licenseId) {
                const platformFeaturesResponse = await apiDispatch(getLicenseAvailableFeatures({licenseId}));
                if (platformFeaturesResponse.payload.success) {
                    setInitialPlatformFeatures(platformFeaturesResponse.payload.data.map(({code}) => code));
                    setPlatformFeatures(platformFeaturesResponse.payload.data.map(({code}) => code));
                }
            } else {
                setInitialPlatformFeatures(loadedDefaultPlatformFeatures);
                setPlatformFeatures(loadedDefaultPlatformFeatures);
            }
            setLoading(false);
        };

        if (licenseType) {
            loadPlatformFeatures(licenseType);
        }
    }, [licenseId, accountId, licenseType, disabled, apiDispatch]);

    const onSavePlatformFeatures = useCallback(async (createdLicenseId = licenseId) => {
        let error: string | null = null;

        if (createdLicenseId) {
            const savePlatformFeaturesResponse = await apiDispatch(saveLicenseFeatures({
                licenseId: createdLicenseId,
                requestBody: platformFeatures,
            }));
            if (savePlatformFeaturesResponse.payload.error) {
                error = getError({error: savePlatformFeaturesResponse.payload.data, separator: ' '});
            } else {
                setInitialPlatformFeatures(platformFeatures);
            }
        }

        return error;
    }, [licenseId, platformFeatures, apiDispatch]);

    const onDiscardPlatformFeatures = useCallback(() => {
        if (initialPlatformFeatures) {
            setPlatformFeatures(initialPlatformFeatures);
        }
    }, [initialPlatformFeatures]);

    const onResetPlatformFeatures = useCallback(() => {
        if (defaultPlatformFeatures) {
            setPlatformFeatures(defaultPlatformFeatures);
        }
    }, [defaultPlatformFeatures]);

    const isDefaultPlatformFeatures = useMemo(() => (defaultPlatformFeatures
        ? isDeepEqual({prev: defaultPlatformFeatures, current: platformFeatures})
        : true
    ), [defaultPlatformFeatures, platformFeatures]);

    const isInitialPlatformFeatures = useMemo(() => (initialPlatformFeatures
        ? isDeepEqual({prev: initialPlatformFeatures, current: platformFeatures})
        : true
    ), [initialPlatformFeatures, platformFeatures]);

    return {
        loading,
        pluginsAppsTree,
        exportOptionsTree,
        platformFeatures,
        isInitialPlatformFeatures,
        isDefaultPlatformFeatures,
        setPlatformFeatures,
        onSavePlatformFeatures,
        onDiscardPlatformFeatures,
        onResetPlatformFeatures,
    };
};
